import { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation, useSearchParams } from "react-router-dom";
import { useFetchPatient } from "@/infra/apis/patients/useFetchPatient";
import { useFetchActiveTreatment } from "@/infra/apis/treatments/useFetchActiveTreatment";
import { useIntl } from "@/infra/locale";
import { analyticsService } from "@/infra/observability/analyticsService";
import { checkInvalidPrescriptionState, checkPrescriptionExpired } from "@/utils/prescriptionUtils";
import {
  useAuth,
  useFetchTermsAndConditionsStatus,
  useNavigateToTermsAndConditions,
  errorTrackingService,
  Exceptions,
} from "@akina/infra";
import {
  LoadingOverlay,
  useLocaleStore,
  useScreenSizeDetector,
  UpdatedPrivacyPolicyAcceptanceModal,
  UpdatedTermsAcceptanceModal,
  UpdatedTermsAndPrivacyPolicyAcceptanceModal,
  ExternalLink,
  TextLink,
} from "@akina/presentation";
import { injectLinks } from "@akina/utils";
import { AppRoutes } from ".";
import { LOCALE_GET_PARAM, getUrlWithLocale, isMobileBlockedRoute } from "./utils";

const ProtectedRoute = () => {
  const { pathname } = useLocation();
  const { isMobile } = useScreenSizeDetector();

  // Data Fetching
  const { isAuthenticated, isLoading: isAuthLoading, user } = useAuth();
  const { isLoading: isPatientLoading, data: patient, error: patientError } = useFetchPatient({ throwOnError: false });
  const {
    isLoading: isActiveTreatmentLoading,
    data: activeTreatment,
    error: activeTreatmentError,
  } = useFetchActiveTreatment();
  const isProtectedDataLoading = isPatientLoading || isActiveTreatmentLoading;

  // Search Params
  const [searchParams] = useSearchParams();
  const locale = searchParams.get(LOCALE_GET_PARAM);
  const search = locale ? `?${LOCALE_GET_PARAM}=${locale}` : "";

  const isOnboardingVerificationPage = AppRoutes.OnboardingVerify === pathname;
  const isInvalidPrescriptionState = checkInvalidPrescriptionState(activeTreatment);
  const isPatientDisconnected = !isProtectedDataLoading && activeTreatmentError instanceof Exceptions[404];
  const isPrescriptionExpired = !isProtectedDataLoading && checkPrescriptionExpired(patient, patientError);

  usePatientInitialization(patient);

  if (isAuthLoading) {
    return <LoadingOverlay />;
  }
  if (!isAuthenticated) return <Navigate to={{ pathname: AppRoutes.Onboarding, search }} />;
  if (isOnboardingVerificationPage) return <Outlet />;
  if (!user?.email_verified) return <Navigate to={AppRoutes.OnboardingVerify} />;
  if (isProtectedDataLoading) {
    return <LoadingOverlay />;
  }

  if (isInvalidPrescriptionState && (pathname as AppRoutes) !== AppRoutes.InvalidPrescriptionState)
    return <Navigate to={AppRoutes.InvalidPrescriptionState} replace />;
  if (isPrescriptionExpired && (pathname as AppRoutes) !== AppRoutes.PrescriptionExpired)
    return <Navigate to={AppRoutes.PrescriptionExpired} replace />;
  if (!isPrescriptionExpired && isPatientDisconnected && (pathname as AppRoutes) !== AppRoutes.PatientDisconnected)
    return <Navigate to={AppRoutes.PatientDisconnected} replace />;

  // Small screens never reach the session route: Today explains why and offers
  // the mobile app instead (SessionUnavailableOnMobile).
  if (isMobile && isMobileBlockedRoute(pathname)) return <Navigate to={AppRoutes.DashboardToday} replace />;

  return (
    <>
      <LegalModals />
      <Outlet />
    </>
  );
};

function LegalModals() {
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [privacyPolicyModalOpen, setPrivacyPolicyModalOpen] = useState(false);
  const [termsAndPrivacyPolicyModalOpen, setTermsAndPrivacyPolicyModalOpen] = useState(false);

  const { formatMessage } = useIntl();
  const locale = useLocaleStore(state => state.locale);
  const { data: patientTerms } = useFetchTermsAndConditionsStatus();
  const { pathname } = useLocation();

  const privacyPolicyUrl = getUrlWithLocale(AppRoutes.PrivacyPolicyAndDataTransparency, locale);
  const termsUrl = getUrlWithLocale(AppRoutes.TermsAndConditions, locale);

  useNavigateToTermsAndConditions({
    terms: patientTerms,
    setTermsModalOpen,
    setPrivacyPolicyModalOpen,
    setTermsAndPrivacyPolicyModalOpen,
  });

  if (!patientTerms || isOnboardingOrLegalPage(pathname as AppRoutes)) return null;

  const tosLabel = formatMessage({ id: "shared.legal.termsAndConditions" });
  const ppLabel = formatMessage({ id: "shared.legal.privacyPolicy" });

  const tosLink = (
    <ExternalLink href={termsUrl}>
      <TextLink size="base" className="inline whitespace-nowrap">
        {tosLabel}
      </TextLink>
    </ExternalLink>
  );
  const ppLink = (
    <ExternalLink href={privacyPolicyUrl}>
      <TextLink size="base" className="inline whitespace-nowrap">
        {ppLabel}
      </TextLink>
    </ExternalLink>
  );

  return (
    <>
      <UpdatedTermsAcceptanceModal
        open={termsModalOpen}
        onOpenChange={setTermsModalOpen}
        app="care"
        entity="patient"
        messages={{
          title: formatMessage({ id: "shared.legal.tosUpdate.title" }),
          description: injectLinks(formatMessage({ id: "shared.legal.tosUpdate.description" }), [
            { label: "{termsOfService}", element: tosLink },
          ]),
          buttonLabel: formatMessage({ id: "shared.legal.tosUpdate.gotItButton" }),
        }}
      />
      <UpdatedPrivacyPolicyAcceptanceModal
        open={privacyPolicyModalOpen}
        onOpenChange={setPrivacyPolicyModalOpen}
        app="care"
        entity="patient"
        messages={{
          title: formatMessage({ id: "shared.legal.privacyPolicyUpdate.title" }),
          description: formatMessage(
            { id: "shared.legal.privacyPolicyUpdate.description" },
            { privacyPolicy: ppLabel },
          ),
          buttonLabel: formatMessage({ id: "shared.legal.privacyPolicyUpdate.acceptButton" }),
          subtext: injectLinks(formatMessage({ id: "shared.legal.privacyPolicyUpdate.subDescription" }), [
            { label: "{privacyPolicy}", element: ppLink },
          ]),
        }}
      />
      <UpdatedTermsAndPrivacyPolicyAcceptanceModal
        open={termsAndPrivacyPolicyModalOpen}
        onOpenChange={setTermsAndPrivacyPolicyModalOpen}
        app="care"
        entity="patient"
        messages={{
          title: formatMessage({ id: "shared.legal.privacyAndTosUpdate.title" }),
          description: formatMessage(
            { id: "shared.legal.privacyAndTosUpdate.description" },
            { termsOfService: tosLabel, privacyPolicy: ppLabel },
          ),
          buttonLabel: formatMessage({ id: "shared.legal.privacyAndTosUpdate.acceptButton" }),
          subtext: injectLinks(formatMessage({ id: "shared.legal.privacyAndTosUpdate.subDescription" }), [
            { label: "{termsOfService}", element: tosLink },
            { label: "{privacyPolicy}", element: ppLink },
          ]),
        }}
      />
    </>
  );
}

function usePatientInitialization(patient: ReturnType<typeof useFetchPatient>["data"]) {
  const { setLocale } = useLocaleStore();

  useEffect(() => {
    if (!patient?.patientId) return;
    analyticsService.identifyUser({ id: patient.patientId });
    errorTrackingService.identifyUser({ id: patient.patientId });
  }, [patient?.patientId]);

  useEffect(() => {
    if (patient?.preferences?.language) {
      setLocale(patient.preferences.language);
    }
  }, [patient?.preferences?.language, setLocale]);
}

function isOnboardingOrLegalPage(pathname: AppRoutes): boolean {
  return (
    pathname === AppRoutes.PrivacyPolicyAndDataTransparency ||
    pathname === AppRoutes.TermsAndConditions ||
    pathname === AppRoutes.OnboardingVerify ||
    pathname === AppRoutes.OnboardingTermsAndConditions ||
    pathname === AppRoutes.Onboarding
  );
}

export default ProtectedRoute;
